import React from 'react';
import {
  Box,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { useRedirect } from 'react-admin';

interface Franchisee {
  id: string;
  code: string;
  name: string;
  franchiseeOwner?: string;
  franchiseeEmail?: string;
  franchiseePhone?: string;
  complianceScore?: number;
  royaltyRate?: number;
  brand?: { name: string };
  region?: { name: string };
}

interface FilterOption {
  id: string;
  name: string;
}

interface FranchisesTableProps {
  franchisees: Franchisee[];
  loading?: boolean;
  error?: string;
  onRowClick?: (franchisee: Franchisee) => void;
  showBrand?: boolean;
  showRegion?: boolean;
  brands?: FilterOption[];
  regions?: FilterOption[];
  selectedBrandId?: string;
  selectedRegionId?: string;
  onBrandChange?: (brandId: string) => void;
  onRegionChange?: (regionId: string) => void;
  showFilters?: boolean;
}

const getComplianceStatus = (score: number | null | undefined) => {
  const s = score ?? 0;
  if (s >= 95) return { color: '#388e3c', label: 'Excellent' };
  if (s >= 85) return { color: '#f57c00', label: 'Good' };
  return { color: '#d32f2f', label: 'Critical' };
};

const formatPercentage = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

export const FranchisesTable = ({
  franchisees,
  loading = false,
  error = null,
  onRowClick,
  showBrand = true,
  showRegion = true,
  brands = [],
  regions = [],
  selectedBrandId = '',
  selectedRegionId = '',
  onBrandChange,
  onRegionChange,
  showFilters = false,
}: FranchisesTableProps) => {
  const redirect = useRedirect();

  if (loading && franchisees.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Calculate column widths based on what we're showing
  const columnCount = 5 + (showBrand ? 1 : 0) + (showRegion ? 1 : 0);
  const codeWidth = '10%';
  const franchiseOwnerWidth = '18%';
  const contactWidth = '30%';
  const complianceWidth = '12%';
  const royaltyWidth = '10%';
  const brandRegionWidth = (100 - 10 - 18 - 30 - 12 - 10) / (showBrand && showRegion ? 2 : 1) + '%';

  return (
    <Box>
      {/* Filters */}
      {showFilters && (brands.length > 0 || regions.length > 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              {brands.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={selectedBrandId}
                      onChange={(e) => onBrandChange?.(e.target.value)}
                      label="Brand"
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {regions.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={brands.length > 0 && !selectedBrandId}>
                    <InputLabel>Region</InputLabel>
                    <Select
                      value={selectedRegionId}
                      onChange={(e) => onRegionChange?.(e.target.value)}
                      label="Region"
                    >
                      <MenuItem value="">
                        {brands.length > 0 && !selectedBrandId ? 'Select a brand first' : 'All Regions'}
                      </MenuItem>
                      {regions.map((region) => (
                        <MenuItem key={region.id} value={region.id}>
                          {region.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {franchisees.length === 0 ? (
        <Alert severity="info">
          No franchises found
        </Alert>
      ) : (
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', width: codeWidth, minWidth: 90 }}>
              Code
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: franchiseOwnerWidth, minWidth: 150 }}>
              Franchise & Owner
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: contactWidth, minWidth: 240 }}>
              Contact
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: complianceWidth, minWidth: 110, textAlign: 'center' }}>
              Compliance
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: royaltyWidth, minWidth: 90, textAlign: 'center' }}>
              Royalty
            </TableCell>
            {showBrand && (
              <TableCell sx={{ fontWeight: 'bold', width: brandRegionWidth, minWidth: 100 }}>
                Brand
              </TableCell>
            )}
            {showRegion && (
              <TableCell sx={{ fontWeight: 'bold', width: brandRegionWidth, minWidth: 100 }}>
                Region
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {franchisees.map((franchisee) => {
            const complianceStatus = getComplianceStatus(franchisee.complianceScore);
            const complianceScore = franchisee.complianceScore ?? 0;

            return (
              <TableRow
                key={franchisee.id}
                hover
                onClick={() => onRowClick ? onRowClick(franchisee) : redirect(`/tenants/${franchisee.code}/show`)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(28, 81, 149, 0.05)' },
                  height: 70,
                }}
              >
                <TableCell>
                  <Chip
                    label={franchisee.code}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {franchisee.name}
                    </Typography>
                    {franchisee.franchiseeOwner && (
                      <Typography variant="caption" color="text.secondary">
                        {franchisee.franchiseeOwner}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {franchisee.franchiseeEmail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <EmailIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'primary.main',
                            wordBreak: 'break-word',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {franchisee.franchiseeEmail}
                        </Typography>
                      </Box>
                    )}
                    {franchisee.franchiseePhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <PhoneIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          {franchisee.franchiseePhone}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {complianceScore !== null && complianceScore !== undefined ? (
                    <Chip
                      label={`${complianceScore.toFixed(0)}%`}
                      size="medium"
                      sx={{
                        bgcolor: complianceStatus.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        width: '80px',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {franchisee.royaltyRate !== null && franchisee.royaltyRate !== undefined ? (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatPercentage(franchisee.royaltyRate)}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                {showBrand && (
                  <TableCell>
                    <Chip
                      label={franchisee.brand?.name || '-'}
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                )}
                {showRegion && (
                  <TableCell>
                    <Typography variant="body2">
                      {franchisee.region?.name || '-'}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
      )}
    </Box>
  );
};
